import { useState, type FormEvent } from 'react';
import useScrollMotion from '../../hooks/useScrollMotion';
import usePageMeta from '../../hooks/usePageMeta';
import { CONTACT, CONTACT_FORM_ENDPOINT } from '../../config/contact';
import { CONTACT_PAGE, META } from '../../content/site';
import { ExternalIcon, PhoneIcon, PinIcon } from '../../components/Icons/Icons';
import m from '../../styles/marketing.module.css';
import styles from './Contact.module.css';

type Status = 'idle' | 'sending' | 'sent' | 'error' | 'unconfigured';

const EMPTY = { firstName: '', lastName: '', email: '', message: '' };

const Contact = () => {
  usePageMeta('Contact', META.contactDescription);
  const root = useScrollMotion<HTMLDivElement>();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState<Status>('idle');

  const update = (field: keyof typeof EMPTY) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!CONTACT_FORM_ENDPOINT) {
      setStatus('unconfigured');
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(CONTACT_FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus('sent');
      setForm(EMPTY);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div ref={root}>
      <section className={`${m.section} ${styles.head}`}>
        <div className={m.container}>
          <div className={m.eyebrow}>Contact</div>
          <h1 className={`${m.sectionHead} ${styles.headTitle}`}>{CONTACT_PAGE.title}</h1>
          <p className={`${m.lead} ${styles.headLead}`}>{CONTACT_PAGE.lead}</p>
        </div>
      </section>

      <section className={`${m.section} ${m.tint}`}>
        <div className={`${m.container} ${styles.grid}`}>
          {/* The form. */}
          <form className={styles.formCard} onSubmit={submit} data-reveal>
            <div className={styles.row}>
              <label className={styles.field}>
                <span>First name <i aria-hidden="true">*</i></span>
                <input
                  type="text"
                  name="firstName"
                  autoComplete="given-name"
                  required
                  value={form.firstName}
                  onChange={update('firstName')}
                />
              </label>
              <label className={styles.field}>
                <span>Last name <i aria-hidden="true">*</i></span>
                <input
                  type="text"
                  name="lastName"
                  autoComplete="family-name"
                  required
                  value={form.lastName}
                  onChange={update('lastName')}
                />
              </label>
            </div>
            <label className={styles.field}>
              <span>Email <i aria-hidden="true">*</i></span>
              <input
                type="email"
                name="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={update('email')}
              />
            </label>
            <label className={styles.field}>
              <span>Message <i aria-hidden="true">*</i></span>
              <textarea
                name="message"
                rows={6}
                required
                placeholder={CONTACT_PAGE.placeholder}
                value={form.message}
                onChange={update('message')}
              />
            </label>

            <div className={styles.formFoot}>
              <button
                type="submit"
                className={`${m.btn} ${m.btnLg} ${m.btnPrimary}`}
                disabled={status === 'sending'}
              >
                {status === 'sending' ? 'Sending…' : 'Send message'}
              </button>
              <span className={styles.required}>* Required</span>
            </div>

            <div className={styles.status} role="status" aria-live="polite">
              {status === 'sent' && <p className={styles.ok}>Thank you — your message has been sent.</p>}
              {status === 'error' && (
                <p className={styles.err}>
                  Something went wrong sending your message. Please try again or call{' '}
                  <a href={CONTACT.phoneHref}>{CONTACT.phoneDisplay}</a>.
                </p>
              )}
              {status === 'unconfigured' && (
                <p className={styles.err}>
                  Online messages are not being accepted yet. Please call{' '}
                  <a href={CONTACT.phoneHref}>{CONTACT.phoneDisplay}</a> during Bangkok office hours.
                </p>
              )}
            </div>
          </form>

          {/* The office. */}
          <aside className={styles.office} data-reveal data-reveal-delay="90">
            <div className={`${m.photo} ${m.cutRight} ${styles.officePhoto}`}>
              <img
                src="/images/empire-tower-skyline.webp"
                alt="Empire Tower, South Sathorn Road, Bangkok"
                loading="lazy"
              />
            </div>
            <div className={styles.officeCard}>
              <h2>{CONTACT.company}</h2>
              <div className={styles.officeRow}>
                <span className={styles.officeIcon}>
                  <PinIcon />
                </span>
                <address>
                  {CONTACT.addressLines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </address>
              </div>
              <div className={styles.officeRow}>
                <span className={styles.officeIcon}>
                  <PhoneIcon />
                </span>
                <a href={CONTACT.phoneHref} className={styles.phone}>
                  {CONTACT.phoneDisplay}
                </a>
              </div>
              <a
                href={CONTACT.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`${m.btn} ${m.btnMd} ${m.btnOutline}`}
              >
                Open in Google Maps <ExternalIcon width={16} height={16} />
              </a>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
